/**
 * Every card face, fetched as soon as the first screen is up, and again for a
 * deck the player switches to.
 *
 * Without it a card's picture is fetched the first time that card is drawn —
 * which, for another player's play, is the moment it is thrown in, so the
 * first frames of its flight were a blank card (and the tilt glint played over
 * nothing). Fifty-odd small files, most of them SVG, the courts WebP: well
 * under a couple of megabytes a deck, fetched once and cached.
 *
 * ## After the first screen, not with it
 *
 * The deck used to be requested from `main.tsx` before React had rendered
 * anything, and measured on a phone profile it was the slowest thing about
 * starting the app: fifty-four requests queued ahead of the UI font and the
 * screen's own data, and each SVG that arrived parsed as a document of its own
 * on the main thread while the first screen was trying to lay itself out. None
 * of those cards is on the sign-in screen or the home screen, and the ones a
 * table shows first are requested by their own `<img>`s at full priority.
 *
 * So it waits for the page's `load` and the UI font, and then asks for one
 * face per idle period the browser offers with room to spare, at low fetch
 * priority. Pacing the requests paces the parses too, since each SVG is parsed
 * as it arrives, so the deck never lands in a frame anyone sees; done in a
 * burst it was a dropped frame or four in the middle of the deal.
 *
 * (It once fetched the whole deck with `fetch()` first, to separate the
 * download from the parse. A `fetch` and an `<img>` asking for the same URL at
 * once could leave the image half-loaded for good — measured, a court in the
 * hand that never finished — so images are all it uses.)
 */
import type { CardId } from '@cards/shared'
import { cardSrc, type CardArt, type DeckName } from './assets'
import { getSettings, subscribeSettings } from '../settings/settings'

const SUITS = ['S', 'H', 'D', 'C'] as const
const RANKS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14] as const

/** Decks asked for. */
const asked = new Set<DeckName>()
/** Kept so the browser holds the parsed faces rather than collecting them. */
const images: HTMLImageElement[] = []

export function everyCard(): CardArt[] {
  const out: CardArt[] = ['BACK', 'JOKER' as CardId]
  for (const suit of SUITS) for (const rank of RANKS) out.push(`${rank}${suit}` as CardId)
  return out
}

/** Faces waiting to be asked for, across every deck. */
const queue: string[] = []
let scheduled = false

type Deadline = { timeRemaining: () => number; didTimeout?: boolean }
const idle: (cb: (d: Deadline) => void) => void =
  typeof requestIdleCallback === 'function'
    ? (cb) => requestIdleCallback(cb, { timeout: 1000 })
    : // Safari has no idle callback. A frame's worth of nothing, a tenth of a
      // second apart, is the nearest thing it offers.
      (cb) => {
        window.setTimeout(() => {
          const end = performance.now() + 12
          cb({ timeRemaining: () => end - performance.now() })
        }, 100)
      }

/** What a face can take of one idle period: anything less and the period is
 *  the tail of a busy frame, and the parse could push into the next one. */
const ROOM_MS = 8

function pump(deadline: Deadline): void {
  scheduled = false
  if (deadline.timeRemaining() >= ROOM_MS || deadline.didTimeout) {
    const src = queue.shift()
    if (src) {
      const img = new Image()
      img.decoding = 'async'
      ;(img as HTMLImageElement & { fetchPriority?: string }).fetchPriority = 'low'
      img.src = src
      // A court is a bitmap, and decoding it now (off the main thread) means
      // its first frame on the table is its face. An SVG is drawn afresh at
      // whatever size it is shown, so decoding one here would only rasterise
      // it at its own 210×315, which nothing displays.
      if (src.endsWith('.webp')) img.decode?.().catch(() => {})
      images.push(img)
    }
  }
  schedule()
}

function schedule(): void {
  if (scheduled || queue.length === 0) return
  scheduled = true
  idle(pump)
}

export function preloadDeck(deck: DeckName): void {
  if (asked.has(deck) || typeof Image === 'undefined') return
  asked.add(deck)
  for (const card of everyCard()) queue.push(cardSrc(card, deck))
  schedule()
}

/** Start with the deck in use once the page has loaded, and follow the
 *  setting. */
export function initPreload(): void {
  if (typeof window === 'undefined') return
  const begin = () => {
    preloadDeck(getSettings().deck)
    subscribeSettings((s) => preloadDeck(s.deck))
  }
  // The page's `load` does not wait for the UI font, and a screen with no
  // images (sign-in, home) fires it before the font is even asked for; fifty
  // fetches started then are fifty requests the font queues behind — on slow
  // 4G, three seconds of them. `document.fonts.ready` is no guard, because it
  // resolves at once while nothing has asked for the font yet; `load()` asks
  // for it, and resolves when it is here (or has failed).
  const afterFonts = () => void document.fonts.load('1em Archivo').then(begin, begin)
  if (document.readyState === 'complete') afterFonts()
  else window.addEventListener('load', afterFonts, { once: true })
}
